/**
 * AI 验收结论相关配色常量
 * 枚举值来自 API: AcceptanceConclusionEnum
 * 配色来自 Figma 设计稿（非 Tailwind 预设色）
 */
import type { AcceptanceConclusionEnum } from '@mpc/common/src/services/api';

/** Alert type 映射 */
export const AI_CONCLUSION_ALERT_TYPE_MAP: Record<
  AcceptanceConclusionEnum,
  'success' | 'warning' | 'error'
> = {
  PASS: 'success',
  CONDITIONAL_PASS_LOW_RISK: 'warning',
  CONDITIONAL_PASS_MID_RISK: 'warning',
  REJECT: 'error',
};

/** 状态灯配色方案（来自 Figma） */
export const AI_CONCLUSION_STYLE_MAP: Record<
  AcceptanceConclusionEnum,
  {
    label: string;
    bg: string;
    border: string;
    textColor: string;
  }
> = {
  PASS: {
    label: '全部通过',
    bg: '#E0FFEC',
    border: '#77E5A9',
    textColor: '#0D7D3B',
  },
  CONDITIONAL_PASS_LOW_RISK: {
    label: '有条件通过-低风险',
    bg: '#FFF6E5',
    border: '#FAC069',
    textColor: '#9E6C00',
  },
  CONDITIONAL_PASS_MID_RISK: {
    label: '有条件通过-中风险',
    bg: '#FFF6E5',
    border: '#FAC069',
    textColor: '#9E6C00',
  },
  REJECT: {
    label: '不通过',
    bg: '#FFEBE6',
    border: '#FFA694',
    textColor: '#C4320A',
  },
};


/** Tag 预设 color 映射（用于批量验收列等表格场景） */
export const AI_CONCLUSION_TAG_COLOR_MAP: Record<AcceptanceConclusionEnum, string> = {
  PASS: 'light-green',
  CONDITIONAL_PASS_LOW_RISK: 'light-orange',
  CONDITIONAL_PASS_MID_RISK: 'light-orange',
  REJECT: 'light-red',
};

/** 判断是否为"有条件通过"结论 */
export const isConditionalPass = (conclusion?: string): boolean =>
  conclusion === 'CONDITIONAL_PASS_LOW_RISK' ||
  conclusion === 'CONDITIONAL_PASS_MID_RISK';

/** 判断是否为"不通过"结论 */
export const isReject = (conclusion?: string): boolean =>
  conclusion === 'REJECT';

/** 判断是否为"全部通过"结论 */
export const isPass = (conclusion?: string): boolean =>
  conclusion === 'PASS';

/**
 * 将 markdown 字符串转为 HTML 并在新标签页中展示
 * @param mdContent markdown 格式的分析报告内容
 */
export const openMarkdownReport = (mdContent: string) => {
  import('marked').then(({ marked }) => {
    // 转换转义的换行符为真实换行符
    const normalizedContent = mdContent.replace(/\\n/g, '\n');
    const htmlBody = marked.parse(normalizedContent) as string;
    const fullHtml = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>AI验收分析报告</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      max-width: 960px;
      margin: 0 auto;
      padding: 40px 24px;
      color: #1a1a1a;
      line-height: 1.8;
      background: #fff;
    }
    h1 { font-size: 24px; border-bottom: 1px solid #e8e8e8; padding-bottom: 12px; }
    h2 { font-size: 20px; margin-top: 32px; }
    h3 { font-size: 16px; margin-top: 24px; }
    table { border-collapse: collapse; width: 100%; margin: 16px 0; }
    th, td { border: 1px solid #d9d9d9; padding: 8px 12px; text-align: left; }
    th { background: #fafafa; }
    code { background: #f5f5f5; padding: 2px 6px; border-radius: 4px; font-size: 13px; }
    pre { background: #f5f5f5; padding: 16px; border-radius: 8px; overflow-x: auto; }
    pre code { background: none; padding: 0; }
    blockquote { border-left: 4px solid #d9d9d9; margin: 16px 0; padding: 8px 16px; color: #666; }
    img { max-width: 100%; }
    ul, ol { padding-left: 24px; }
  </style>
</head>
<body>${htmlBody}</body>
</html>`;
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(fullHtml);
      newWindow.document.close();
    }
  });
};
